<template>
<a v-if="visible" href="javascript:void(0)" @click="goCart" class="fcb" :class="{'fcb-light': !darkMode}" :title="count ? count + ' item' + (count===1?'':'s') + ' in cart' : 'Your cart is empty'">
  <i class="mdi mdi-cart"></i>
  <span v-if="count" class="fcb-badge">{{ count > 99 ? '99+' : count }}</span>
</a>
</template>

<style scoped>
.fcb {
  position: fixed;
  bottom: calc(24px + env(safe-area-inset-bottom, 0px));
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #BFA164;
  color: #1A1A1A;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  text-decoration: none;
  box-shadow: 0 6px 20px rgba(0,0,0,0.4), 0 0 0 4px rgba(191,161,100,0.18);
  z-index: 950;
  transition: transform .15s ease, box-shadow .15s ease;
  cursor: pointer;
}
.fcb:hover { transform: translateY(-2px) scale(1.05); box-shadow: 0 10px 28px rgba(0,0,0,0.45), 0 0 0 6px rgba(191,161,100,0.22); }
.fcb-light { box-shadow: 0 6px 20px rgba(0,0,0,0.15), 0 0 0 4px rgba(191,161,100,0.18); }
.fcb-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #FF6B6B;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 12px;
  padding: 2px 7px;
  min-width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  border: 2px solid #1A1A1A;
}
.fcb-light .fcb-badge { border-color: #FFFFFF; }

/* On mobile, lift above the bottom tab bar (~82px tall) + safe-area; size tightens too */
@media (max-width: 768px) {
  .fcb { width: 50px; height: 50px; right: 16px; font-size: 22px;
         bottom: calc(96px + env(safe-area-inset-bottom, 0px)); }
}
</style>

<script>
/* eslint-disable */
import { execute, flocker } from "@/api";

// Show only on the routes where browsing/shopping happens. Hide everywhere else (admin, vet, farm,
// transport, messaging, checkout flow itself, etc.) to keep the screen clean.
var SHOW_PREFIXES = [
  '/',             // landing (matched as exact)
  '/marketplace',  // marketplace home, search, listings, seller profile, category, saved, offers, buy requests
  '/shop',         // agro-input shop catalogue + product detail
  '/dashboard'     // user dashboard — Cart banner exists but FAB keeps the cart one tap away as the user scrolls
];
var HIDE_PREFIXES = [
  '/cart',         // already on the cart
  '/admin',        // admin area
  '/login', '/register', '/reset-password',
  '/messages', '/notifications',
  '/checkout',     // mid-checkout flow — distracting
  '/farm',         // farm management
  '/vet',          // vet flows
  '/transport',    // transport flows
  '/my-transports',
  '/wallet', '/orders', '/escrow', '/dispute', '/reviews', '/verification', '/insurance', '/market', '/learn', '/forum',
  '/sell',         // create/edit listing flows (seller side)
  '/my-listings',
  '/marketplace/listing'  // page already has its own sticky bottom Cart/Offer bar
];

export default {
  name: "FloatingCartButton",
  data(){return{
    count: Number(sessionStorage.getItem('flockr_cart_count') || 0),
    darkMode: (document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark') !== 'light',
    themeObserver: null,
    fetched: false
  }},
  computed:{
    visible(){
      // Only logged in users
      if (!sessionStorage.getItem('userId')) return false;
      var path = this.$route && this.$route.path ? this.$route.path : '/';
      // Hide first — explicit hides win over shows
      for (var i = 0; i < HIDE_PREFIXES.length; i++) {
        var hp = HIDE_PREFIXES[i];
        if (path === hp || path.indexOf(hp + '/') === 0) return false;
      }
      // Special-case the literal '/' (don't match every path that starts with '/')
      if (path === '/') return true;
      for (var j = 0; j < SHOW_PREFIXES.length; j++) {
        var sp = SHOW_PREFIXES[j];
        if (sp === '/') continue;
        if (path === sp || path.indexOf(sp + '/') === 0) return true;
      }
      return false;
    }
  },
  // No watch on $route — that was firing getCartCount on every navigation and serializing other
  // requests behind it on slow links (browsers cap concurrent HTTP/1.1 connections per host).
  // Cart count stays fresh via the `flockr-cart-updated` event (dispatched on add/remove) plus
  // a one-time fetch on mount. Stale-by-one-action is fine; blocking the app isn't.
  mounted(){
    var self = this;
    this._onCartUpdated = function(e){
      if (e && e.detail) self.count = Number(e.detail.count || 0);
    };
    window.addEventListener('flockr-cart-updated', this._onCartUpdated);

    this.syncTheme();
    this.themeObserver = new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    // Pull a fresh count on first mount
    this.fetchCount();
  },
  beforeUnmount(){
    if (this._onCartUpdated) window.removeEventListener('flockr-cart-updated', this._onCartUpdated);
    if (this.themeObserver) this.themeObserver.disconnect();
  },
  methods:{
    syncTheme(){ this.darkMode = document.documentElement.getAttribute('data-theme') !== 'light'; },
    async fetchCount(){
      var userId = sessionStorage.getItem('userId');
      if (!userId) { this.count = 0; return; }
      // Defensive 4-second timeout via Promise.race — if the API is slow we don't keep the user
      // staring at a stale badge forever. The underlying axios call may keep running in the
      // background but we stop awaiting it.
      var self = this;
      try {
        var f = new FormData(); f.append('function', 'getCartCount'); f.append('userId', userId);
        var r = await Promise.race([
          execute(f, flocker),
          new Promise(function(_, reject){ setTimeout(function(){ reject(new Error('cart count timeout')); }, 4000); })
        ]);
        if (r && r.data && r.data.success && r.data.data) {
          self.count = Number(r.data.data.cartCount || 0);
          sessionStorage.setItem('flockr_cart_count', self.count);
        }
      } catch (e) { /* timeout or network error — keep cached count */ }
    },
    goCart(){
      this.$router.push('/cart');
    }
  }
};
</script>
